import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { requestSchema } from '../lib/validate'
import { submitRequest, type ApiSuccess } from '../lib/api-client'
import { errorToMessage } from '../lib/error-message'
import { t, type Lang } from '../lib/i18n'
import { FormField, inputClasses, textareaClasses, selectClasses } from './form-field'
import { SubmitButton } from './submit-button'
import { SuccessState } from './success-state'
import { Turnstile } from './turnstile'

// Server schema includes turnstileToken; client form gathers fields, token comes
// from the widget.
const formSchema = requestSchema.omit({ turnstileToken: true })
type FormData = z.infer<typeof formSchema>

type State =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success'; result: ApiSuccess }
  | { status: 'error'; message: string }

interface RequestFormProps {
  lang: Lang
}

export function RequestForm({ lang }: RequestFormProps) {
  const [state, setState] = useState<State>({ status: 'idle' })
  const [token, setToken] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { kind: 'course', lang, title: '', description: '', email: '' },
    mode: 'onBlur',
  })

  const description = watch('description') ?? ''
  const title = watch('title') ?? ''

  const onSubmit = async (data: FormData) => {
    if (!token) {
      setState({ status: 'error', message: t(lang, 'error.turnstile_pending') })
      return
    }
    setState({ status: 'submitting' })
    const res = await submitRequest({ ...data, turnstileToken: token })
    if (res.ok) {
      setState({ status: 'success', result: res })
    } else {
      setState({ status: 'error', message: errorToMessage(res, lang) })
      // Token is single-use; force user to re-verify.
      setToken(null)
    }
  }

  if (state.status === 'success') {
    return (
      <SuccessState
        lang={lang}
        variant="request"
        issueNumber={state.result.issueNumber}
        issueUrl={state.result.url}
        onReset={() => {
          reset()
          setToken(null)
          setState({ status: 'idle' })
        }}
      />
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      <FormField
        label={t(lang, 'request.field.kind')}
        required
        error={errors.kind?.message}
      >
        <select {...register('kind')} className={selectClasses}>
          <option value="course">{t(lang, 'request.kind.course')}</option>
          <option value="module">{t(lang, 'request.kind.module')}</option>
        </select>
      </FormField>

      <FormField
        label={t(lang, 'request.field.title')}
        required
        helper={t(lang, 'request.field.title.help')}
        error={errors.title?.message}
        counter={{ current: title.length, max: 120 }}
      >
        <input
          {...register('title')}
          type="text"
          className={inputClasses}
          maxLength={120}
          autoComplete="off"
        />
      </FormField>

      <FormField
        label={t(lang, 'request.field.description')}
        required
        helper={t(lang, 'request.field.description.help')}
        error={errors.description?.message}
        counter={{ current: description.length, max: 1000 }}
      >
        <textarea
          {...register('description')}
          className={textareaClasses}
          maxLength={1000}
          rows={6}
        />
      </FormField>

      <FormField
        label={t(lang, 'request.field.email')}
        helper={t(lang, 'request.field.email.help')}
        error={errors.email?.message}
      >
        <input
          {...register('email')}
          type="email"
          className={inputClasses}
          autoComplete="email"
          inputMode="email"
        />
      </FormField>

      <Turnstile onToken={setToken} onExpired={() => setToken(null)} />

      {state.status === 'error' && (
        <p role="alert" className="text-sm text-danger">
          {state.message}
        </p>
      )}

      <SubmitButton
        isSubmitting={state.status === 'submitting'}
        disabled={!token}
        label={t(lang, 'request.submit')}
        loadingLabel={t(lang, 'request.submit.loading')}
      />
    </form>
  )
}
